import { useEffect, useState, useCallback } from "react";
import { NewsItem } from "../Types/Types";

const apiUrl = process.env.REACT_APP_API_URL;
const baseUrl = `${apiUrl}/api/news`;

const useNews = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    const lang = localStorage.getItem("lang") || "en";
    try {
      const res = await fetch(
        `${baseUrl}?lang=${lang}&search=${encodeURIComponent(searchQuery)}&limit=100`
      );
      if (!res.ok) throw new Error("Failed to fetch news");
      const data = await res.json();
      setNews(data.news || []);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const createNews = async (formData: FormData): Promise<boolean> => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(baseUrl, {
        method: "POST",
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to create news");
      await fetchNews();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const updateNews = async (id: string, formData: FormData): Promise<boolean> => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${baseUrl}/${id}`, {
        method: "PUT",
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to update news");
      await fetchNews();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteNews = async (id: string): Promise<boolean> => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${baseUrl}/${id}`, {
        method: "DELETE",
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      if (!res.ok) throw new Error("Failed to delete news");
      setNews((prev) => prev.filter((n) => n._id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  return {
    news,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    fetchNews,
    createNews,
    updateNews,
    deleteNews,
  };
};

export default useNews;
